import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority";

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        outline:
          "border border-input shadow-sm hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        // Admin samurai theme variants
        adminPrimary:
          "bg-gradient-to-r from-red-800 to-red-900 text-amber-50 hover:from-red-900 hover:to-red-950 border border-amber-600/30 shadow-md focus-visible:ring-amber-500",
        adminSecondary:
          "bg-stone-100 dark:bg-stone-700 text-gray-800 dark:text-gray-200 border-2 border-stone-300 dark:border-stone-600 hover:bg-amber-50 dark:hover:bg-stone-600 hover:border-amber-400 dark:hover:border-amber-500 focus-visible:ring-amber-500",
        adminGold:
          "bg-gradient-to-r from-amber-500 to-amber-600 text-gray-900 hover:from-amber-600 hover:to-amber-700 font-semibold shadow-md focus-visible:ring-amber-500",
        adminOutline:
          "border-2 border-stone-300 dark:border-stone-600 bg-transparent text-gray-700 dark:text-gray-300 hover:bg-stone-50 dark:hover:bg-stone-800 hover:border-amber-500 focus-visible:ring-amber-500",
        adminDestructive:
          "bg-gradient-to-r from-red-600 to-red-700 text-white hover:from-red-700 hover:to-red-800 shadow-md focus-visible:ring-red-500",
        // Member portal "Modern Dojo" theme variants - Crimson + Gold
        memberPrimary:
          "bg-gradient-to-r from-[#9B1B30] to-[#6B1220] text-white shadow-lg shadow-[#9B1B30]/30 hover:from-[#7A1526] hover:to-[#5A0F1A] hover:shadow-xl hover:shadow-[#9B1B30]/40 hover:scale-[1.02] border-0 focus-visible:ring-[#9B1B30] transition-all duration-300 dojo-btn-shine font-semibold",
        memberSecondary:
          "bg-white/90 text-[#1A1814] border border-[#D6D3D1]/50 hover:bg-[#FAF7F2] hover:border-[#C9A227] hover:text-[#1A1814] focus-visible:ring-[#C9A227] transition-all duration-300 font-medium",
        memberOutline:
          "border-2 border-[#C9A227] text-[#C9A227] bg-transparent hover:bg-[#C9A227]/10 hover:border-[#E8D48A] focus-visible:ring-[#C9A227] transition-all duration-300 font-semibold",
        memberGhost:
          "text-[#6B6560] bg-transparent hover:bg-[#1A1814]/5 hover:text-[#1A1814] focus-visible:ring-[#1A1814]/30 transition-all duration-300",
        memberGhostLight:
          "text-[#D6D3D1] bg-transparent hover:bg-white/10 hover:text-white focus-visible:ring-[#C9A227] transition-all duration-300",
        memberGold:
          "bg-gradient-to-r from-[#C9A227] to-[#A88B1E] text-[#1A1814] shadow-md shadow-[#C9A227]/25 hover:from-[#E8D48A] hover:to-[#C9A227] hover:shadow-lg hover:scale-[1.02] border-0 focus-visible:ring-[#C9A227] transition-all duration-300 font-bold",
        memberDestructive:
          "bg-gradient-to-r from-red-600 to-red-700 text-white hover:from-red-700 hover:to-red-800 shadow-md focus-visible:ring-red-500 transition-all duration-300",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-10 rounded-md px-8",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

const Button = React.forwardRef(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button"
  return (
    <Comp
      data-slot="button"
      data-variant={variant || "default"}
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      {...props} />
  );
})
Button.displayName = "Button"

export { Button, buttonVariants }
